import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  CreditCard, 
  MessageCircle, 
  Copy, 
  Link2, 
  AlertTriangle 
} from 'lucide-react';
import { toast } from 'sonner';

interface CardSetupShareModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cardSetupLink: string | null;
  contactName?: string;
  contactPhone?: string;
}

export function CardSetupShareModal({
  open,
  onOpenChange,
  cardSetupLink,
  contactName,
  contactPhone
}: CardSetupShareModalProps) {

  const handleSendWhatsApp = () => {
    if (!cardSetupLink) return;
    
    const firstName = contactName?.split(' ')[0] || 'Cliente';
    const message = `Olá ${firstName}! 👋

Para facilitar seus pagamentos futuros, cadastre seu cartão de forma segura clicando no link abaixo:

🔒 ${cardSetupLink}

Este link é seguro e seus dados são protegidos pelo Stripe.`;

    const phoneNumber = contactPhone?.replace(/\D/g, '') || '';
    const whatsappUrl = phoneNumber 
      ? `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    onOpenChange(false);
    toast.success('WhatsApp aberto com a mensagem');
  };

  const handleCopyLink = async () => {
    if (!cardSetupLink) return;
    
    try {
      await navigator.clipboard.writeText(cardSetupLink);
      toast.success('Link copiado para a área de transferência');
    } catch {
      toast.error('Erro ao copiar link');
    }
  };

  const handleOpenLink = () => {
    if (!cardSetupLink) return;
    window.open(cardSetupLink, '_blank');
    onOpenChange(false);
    toast.info('Link aberto - lembre-se do aviso de segurança');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Enviar Link de Cadastro de Cartão
          </DialogTitle>
          <DialogDescription>
            O cliente deve abrir este link no próprio dispositivo para garantir segurança na transação.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {/* WhatsApp Button */}
          <Button
            variant="outline"
            className="w-full justify-start h-12 text-left"
            onClick={handleSendWhatsApp}
          >
            <MessageCircle className="w-5 h-5 mr-3 text-green-500" />
            <div>
              <div className="font-medium">Enviar por WhatsApp</div>
              <div className="text-xs text-muted-foreground">Recomendado - mais seguro</div>
            </div>
          </Button>

          {/* Copy Link Button */}
          <Button
            variant="outline"
            className="w-full justify-start h-12 text-left"
            onClick={handleCopyLink}
          >
            <Copy className="w-5 h-5 mr-3 text-blue-500" />
            <div>
              <div className="font-medium">Copiar Link</div>
              <div className="text-xs text-muted-foreground">Cole onde preferir</div>
            </div>
          </Button>

          {/* Open Link Button (with warning) */}
          <div className="pt-2 border-t">
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-left text-muted-foreground hover:text-foreground"
              onClick={handleOpenLink}
            >
              <Link2 className="w-5 h-5 mr-3" />
              <div>
                <div className="font-medium">Abrir Link (cliente presente)</div>
                <div className="text-xs">Apenas se o cliente estiver ao seu lado</div>
              </div>
            </Button>
            
            <div className="flex items-start gap-2 mt-2 p-3 bg-amber-500/10 rounded-lg text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Abrir o link no seu dispositivo pode causar bloqueio por fraude no Stripe, 
                pois o IP registrado será o seu, não do cliente.
              </span>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
